import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  MOODS,
  MAX_ATTACHMENTS_PER_ENTRY,
  type Mood,
  type Visibility,
} from "@wodiary/shared";
import { api, ApiError } from "../api/client.js";
import { useEntry, useEntryAttachments, usePairing, useSettings } from "../api/queries.js";
import type { SignedAttachment } from "../api/types.js";
import { useAuth } from "../auth/AuthContext.js";
import { errorMessage } from "../lib/errors.js";
import {
  readImageSize,
  rejectionMessage,
  selectAddable,
} from "../lib/attachments.js";
import { formatTime } from "../lib/format.js";
import { NavBar } from "../components/NavBar.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { VisibilityPrivateModal } from "../components/VisibilityPrivateModal.js";
import { useToast } from "../components/ToastContext.js";
import { LockIcon, ShareIcon } from "../components/icons.js";

type SaveState = "idle" | "dirty" | "saving" | "saved" | "error" | "conflict";

/**
 * 写日记 / 编辑日记（spec §5.3 `ENTRY-1`）。
 *
 * ## 草稿为什么先建为私密
 *
 * 自动保存要求「内容一变就落库」，但一篇还没写完的日记**不能**因为默认可见性
 * 是 shared 就被对方读到。所以草稿一律先以 `private` 落库，用户在底部选择
 * 「发布给 TA」时才把可见性改成 shared。于是「自动保存」与「不泄露半成品」
 * 同时成立。
 *
 * ## 乐观锁
 *
 * 每次 `PATCH` 都带上一次拿到的 `updated_at`。不匹配 → 409 → 显示「这篇在
 * 别处被改过」并提供重新加载。**不静默覆盖**（spec §5.3）。
 */
export function WritePage() {
  const { id } = useParams<{ id: string }>();
  const mode: "create" | "edit" = id === undefined ? "create" : "edit";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const entryQuery = useEntry(mode === "edit" ? id : undefined);
  const pairing = usePairing();
  const settings = useSettings();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState<Mood | null>(null);
  const [allowReply, setAllowReply] = useState(true);
  const [visibility, setVisibility] = useState<Visibility>("shared");

  const [draftId, setDraftId] = useState<string | null>(id ?? null);
  const expectedRef = useRef<string | null>(null);
  const seqRef = useRef(0);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [privateModal, setPrivateModal] = useState(false);
  const [replyCount, setReplyCount] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<SignedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const attachmentsQuery = useEntryAttachments(id, mode === "edit");
  const attachmentsSeededRef = useRef(false);

  const seededRef = useRef(false);
  const defaultSeededRef = useRef(false);

  const entry = entryQuery.data;

  // 同一路由换 id 时组件不会重新挂载 —— 先把「已用旧日记初始化过」的标志复位，
  // 否则从一篇编辑页直接跳到另一篇时，新日记的内容永远填不进表单。
  useEffect(() => {
    seededRef.current = false;
    attachmentsSeededRef.current = false;
    setAttachments([]);
    setAttachError(null);
  }, [id]);

  // 编辑模式：用已存在的日记初始化一次。
  useEffect(() => {
    if (mode !== "edit" || entry === undefined || seededRef.current) return;
    setTitle(entry.title);
    setBody(entry.body);
    setMood(entry.mood);
    setAllowReply(entry.allowReply);
    setVisibility(entry.visibility);
    expectedRef.current = entry.updatedAt;
    seededRef.current = true;
  }, [mode, entry]);

  // 新建模式：可见性的初值来自本人设置，而不是硬编码。
  useEffect(() => {
    if (mode !== "create" || defaultSeededRef.current) return;
    if (settings.data === undefined) return;
    setVisibility(settings.data.profile.default_visibility);
    defaultSeededRef.current = true;
  }, [mode, settings.data]);

  // 编辑模式：把已存在的图片读进来。
  useEffect(() => {
    if (mode !== "edit" || attachmentsSeededRef.current) return;
    const data = attachmentsQuery.data;
    if (data === undefined || !("attachments" in data)) return;
    setAttachments(data.attachments);
    attachmentsSeededRef.current = true;
  }, [mode, attachmentsQuery.data]);

  const markDirty = useCallback(() => {
    seqRef.current += 1;
    setDirty(true);
  }, []);

  /** 把当前内容落库。返回落库后的日记 id；失败返回 `null`。 */
  const persist = useCallback(
    async (opts?: { force?: boolean }): Promise<string | null> => {
      // 图片上传需要一条已存在的草稿，所以它可以 force 绕过「没内容就不建」。
      if (
        opts?.force !== true &&
        title.trim() === "" &&
        body.trim() === ""
      ) {
        setDirty(false);
        return draftId;
      }
    const seq = seqRef.current;
    setSaveState("saving");
    try {
      let nextId = draftId;
      if (nextId === null) {
        const created = await api.createEntry({
          title,
          body,
          mood,
          visibility: "private", // 草稿一律私密，发布时才改
          allow_reply: allowReply,
        });
        nextId = created.id;
        setDraftId(created.id);
        expectedRef.current = created.updatedAt;
      } else {
        const updated = await api.updateEntry(nextId, {
          expected_updated_at: expectedRef.current ?? "",
          title,
          body,
          mood,
          allow_reply: allowReply,
        });
        expectedRef.current = updated.updatedAt;
      }
      if (seqRef.current === seq) {
        setDirty(false);
        setSaveState("saved");
        setSavedAt(new Date().toISOString());
      }
      setError(null);
      return nextId;
    } catch (e) {
      if (e instanceof ApiError && e.code === "entry_conflict") {
        setSaveState("conflict");
        return null;
      }
      if (e instanceof ApiError && e.code === "not_paired") {
        setSaveState("error");
        setError("还没有配对，暂时保存不了。");
        return null;
      }
      setSaveState("error");
      setError(errorMessage(e));
      return null;
    }
  }, [allowReply, body, draftId, mood, title]);

  // 防抖自动保存：内容停下约 0.9 秒后落库（spec §5.3 AC：不产生请求风暴）。
  useEffect(() => {
    if (!dirty) return;
    const t = window.setTimeout(() => void persist(), 900);
    return () => window.clearTimeout(t);
  }, [dirty, title, body, mood, allowReply, persist]);

  async function reseedConflict() {
    if (id === undefined) return;
    const fresh = await api.getEntry(id);
    setTitle(fresh.title);
    setBody(fresh.body);
    setMood(fresh.mood);
    setAllowReply(fresh.allowReply);
    setVisibility(fresh.visibility);
    expectedRef.current = fresh.updatedAt;
    seqRef.current = 0;
    setDirty(false);
    setSaveState("idle");
    queryClient.setQueryData(["entry", id], fresh);
  }

  async function onDone(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    const hasContent = title.trim() !== "" || body.trim() !== "";
    if (!hasContent) {
      navigate(-1);
      return;
    }
    const idNow = await persist();
    if (idNow === null) return;

    if (mode === "create" && visibility === "shared") {
      try {
        await api.setEntryVisibility(idNow, "shared");
      } catch (err) {
        setError(errorMessage(err));
        return;
      }
    }
    await queryClient.invalidateQueries({ queryKey: ["entries"] });
    await queryClient.invalidateQueries({ queryKey: ["entry", idNow] });
    navigate(`/entries/${idNow}`, { replace: mode === "create" });
  }

  async function chooseShared() {
    if (visibility === "shared") return;
    if (mode === "create") {
      setVisibility("shared");
      return;
    }
    if (draftId === null) return;
    try {
      const updated = await api.setEntryVisibility(draftId, "shared");
      setVisibility("shared");
      queryClient.setQueryData(["entry", draftId], updated);
      toast.show("已改为共享");
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function openPrivateModal() {
    if (visibility === "private") return;
    setReplyCount(null);
    if (draftId !== null) {
      try {
        const r = await api.listReplies(draftId);
        setReplyCount(r.replies.length);
      } catch {
        setReplyCount(null);
      }
    }
    setPrivateModal(true);
  }

  async function confirmPrivate() {
    if (draftId === null || visibility === "private") {
      setPrivateModal(false);
      setVisibility("private");
      return;
    }
    try {
      const updated = await api.setEntryVisibility(draftId, "private");
      setVisibility("private");
      queryClient.setQueryData(["entry", draftId], updated);
      setPrivateModal(false);
      toast.show("已改为仅自己可见");
    } catch (err) {
      setError(errorMessage(err));
      setPrivateModal(false);
    }
  }

  async function removeAttachment(attachmentId: string): Promise<void> {
    setAttachError(null);
    try {
      await api.deleteAttachment(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
      if (draftId !== null) {
        await queryClient.invalidateQueries({
          queryKey: ["attachments", draftId],
        });
      }
    } catch (e) {
      setAttachError(errorMessage(e));
    }
  }

  /** 图片上传需要一条已存在的草稿；没有就先建一条（private）。 */
  async function ensureEntryId(): Promise<string | null> {
    if (draftId !== null) return draftId;
    return persist({ force: true });
  }

  async function onFiles(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) return;
    setAttachError(null);
    const { accepted, rejected } = selectAddable(
      Array.from(files),
      attachments.length,
    );
    if (rejected.length > 0) {
      setAttachError(
        rejected
          .map((r) => `${r.file.name}：${rejectionMessage(r.reason)}`)
          .join("；"),
      );
    }
    if (accepted.length === 0) return;
    setUploading(true);
    try {
      const entryId = await ensureEntryId();
      if (entryId === null) {
        setAttachError("图片没能存下，稍后再试。");
        return;
      }
      for (const file of accepted) {
        const dims = await readImageSize(file);
        const uploaded = await api.uploadAttachment(entryId, file, dims);
        setAttachments((prev) => [...prev, uploaded]);
      }
      // 详情页读的是同一个 query key —— 让它别停在旧列表上。
      await queryClient.invalidateQueries({
        queryKey: ["attachments", entryId],
      });
    } catch (e) {
      setAttachError(errorMessage(e));
    } finally {
      setUploading(false);
    }
  }

  if (user === null) return null;

  const paired = pairing.data?.state === "active";
  const partner = pairing.data?.partnerUsername ?? "TA";
  const showUnpairedGate =
    mode === "create" && pairing.data !== undefined && !paired;

  const saveLabel: string =
    saveState === "saving"
      ? "保存中…"
      : saveState === "saved" && savedAt !== null
        ? `草稿已存 ${formatTime(savedAt)}`
        : saveState === "dirty"
          ? "未保存"
          : saveState === "conflict"
            ? "有冲突"
            : saveState === "error"
              ? "保存失败"
              : "";

  return (
    <>
      <NavBar
        title={mode === "create" ? "写日记" : "编辑日记"}
        back
        onBack={() => navigate(-1)}
        right={<span className="save-state">{saveLabel}</span>}
      />

      {mode === "edit" && entryQuery.isLoading && <Loading />}
      {mode === "edit" && entryQuery.isError && (
        <div className="page">
          <QueryError
            error={entryQuery.error}
            onRetry={() => void entryQuery.refetch()}
          />
        </div>
      )}

      {showUnpairedGate ? (
        <div className="page">
          <div className="banner banner-warn">
            <LockIcon size={16} />
            <span>
              还没有配对，写下的日记现在没有地方可去。先完成配对，再回来写。
            </span>
          </div>
          <Link to="/pairing" className="btn btn-primary btn-block">
            去配对
          </Link>
        </div>
      ) : (
        (mode === "create" || entry !== undefined) && (
          <form className="page" onSubmit={(e) => void onDone(e)}>
            {error !== null && <div className="banner banner-error">{error}</div>}
            {saveState === "conflict" && (
              <div className="banner banner-warn">
                <span className="grow">这篇在别处被改过。</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => void reseedConflict()}
                >
                  重新加载
                </button>
              </div>
            )}

            <input
              className="write-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                markDirty();
              }}
              placeholder="写点什么…"
              maxLength={200}
            />
            <textarea
              className="textarea"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                markDirty();
              }}
              placeholder="今天发生了什么…"
            />

            <div className="mood-row">
              {MOODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={mood === m ? "mood on" : "mood"}
                  onClick={() => {
                    setMood(mood === m ? null : m);
                    markDirty();
                  }}
                >
                  {m}
                </button>
              ))}
            </div>

            <div className="stack-sm">
              <div className="row-between">
                <span className="field-label">图片</span>
                <span className="tiny ghost-text mono">
                  {attachments.length} / {MAX_ATTACHMENTS_PER_ENTRY}
                </span>
              </div>
              {attachError !== null && (
                <div className="banner banner-error">{attachError}</div>
              )}
              <div className="thumb-grid">
                {attachments.map((a) => (
                  <div key={a.id} className="thumb">
                    <img className="thumb-img" src={a.url} alt="日记图片" />
                    <button
                      type="button"
                      className="thumb-del"
                      aria-label="删除这张图片"
                      onClick={() => void removeAttachment(a.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS_PER_ENTRY && (
                  <label className="thumb-add">
                    <input
                      type="file"
                      accept="image/*,.heic"
                      multiple
                      hidden
                      disabled={uploading}
                      onChange={(e) => {
                        void onFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                    {uploading ? "上传中…" : "+ 加图"}
                  </label>
                )}
              </div>
              <span className="field-hint">
                单张 ≤2MB，最多 {MAX_ATTACHMENTS_PER_ENTRY} 张。图片随这篇日记的
                可见性一起 —— 日记私密时，图片也只有你能看到。
              </span>
            </div>

            <div className="share-card">
              <div className="row" style={{ gap: 8 }}>
                <ShareIcon size={18} style={{ color: "var(--brown)" }} />
                <h3 style={{ flex: 1 }}>这篇日记给谁看？</h3>
              </div>

              <button
                type="button"
                className={visibility === "shared" ? "share-option on" : "share-option off"}
                onClick={() => void chooseShared()}
                disabled={!paired && mode === "create"}
              >
                <span className="radio" />
                <span className="opt-txt">
                  <span className="t">共享给 {partner}</span>
                  <span className="d">TA 可以看到，可以回应和评论</span>
                </span>
              </button>

              <button
                type="button"
                className={visibility === "private" ? "share-option on" : "share-option off"}
                onClick={() => void openPrivateModal()}
              >
                <span className="radio" />
                <span className="opt-txt">
                  <span className="t">只写给自己</span>
                  <span className="d">TA 完全看不到，随时可以改成共享</span>
                </span>
              </button>

              <hr className="hr-yellow" />

              <div
                className={
                  visibility === "private"
                    ? "switch-row off disabled"
                    : "switch-row off"
                }
              >
                <span className="lb">允许 {partner} 回应</span>
                <button
                  type="button"
                  className={allowReply && visibility === "shared" ? "switch on" : "switch"}
                  role="switch"
                  aria-checked={allowReply && visibility === "shared"}
                  aria-label="允许 TA 回应"
                  disabled={visibility === "private"}
                  onClick={() => {
                    setAllowReply(!allowReply);
                    markDirty();
                  }}
                >
                  <span className="knob" />
                </button>
                <span className="st">
                  {visibility === "private"
                    ? "不适用"
                    : allowReply
                      ? "已开启"
                      : "已关闭"}
                </span>
              </div>
            </div>

            <div className="row" style={{ gap: 10 }}>
              <button
                type="submit"
                className="btn btn-primary grow"
                disabled={saveState === "saving"}
              >
                {visibility === "shared" ? `发布给 ${partner}` : "存为私密"}
              </button>
            </div>
          </form>
        )
      )}

      {privateModal && (
        <VisibilityPrivateModal
          partnerName={partner}
          replyCount={replyCount}
          onCancel={() => setPrivateModal(false)}
          onConfirm={() => void confirmPrivate()}
        />
      )}
    </>
  );
}
